import {
    DESIGN_STANDARDS,
    editAEHAssessment,
    editDesignStandards,
    editEquipmentSpecs,
    editFinalSection,
    editLimitations,
    editSoftwareAssessment,
    limitationFields,
    simplifiedFields
} from "./actions";
import {
    collection,
    getVariantByParentType,
} from "../_variants/index.js";

function makeFieldSideTables(fields) {
    return fields.map(field => {
        field.class = "md:col-span-3";
        field.type = "richtext";
        return {
            "label": field.label,
            "field": true,
            "fields": [field],
            "actions": [{
                "label": `Edit ${field.label}`,
                action({ pageItem, form, meta }) {
                    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
                    const eadtCollection = collection(variant, "EADTs");
                    form.setValues({
                        [field.key]: pageItem[field.key]
                    });
                    form.setConfig({
                        "label": `Edit ${field.label}`,
                        "type": "update",
                        "location": `${eadtCollection}/${pageItem.ID}`,
                        "fields": [{
                            "label": field.label,
                            "name": field.key,
                            "type": "richtext",
                            "divClass": "md:col-span-3",
                            "minimised": true
                        }]
                    });
                    form.toggle();
                },
                show({ item }) {
                    return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
                }
            }]
        };
    });
}

// --- Design Standards ---

const designStandards = {
    "label": "Design Standards",
    "field": true,
    "fields": [{
        "label": "Equipment Design Standard / Qualification",
        "class": "md:col-span-3",
        "grow": true,
        compute({ item }) {
            const ids = item.DesignStandards || [];
            if (!Array.isArray(ids) || ids.length === 0) return "—";
            return ids.map(id => {
                const match = DESIGN_STANDARDS.find(d => d.id === id);
                return match ? match.label : id;
            }).join(", ");
        }
    }],
    "actions": [{
        "label": "Edit Design Standards",
        action(args) {
            editDesignStandards({ ...args, item: args.pageItem });
        },
        show({ item }) {
            return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
        },
    }],
    show({ pageItem }) {
        return pageItem.EADTClassification === "Detailed";
    }
};

// --- Equipment Specifications ---

const equipmentSpecs = {
    "label": "Equipment Specifications",
    "field": true,
    "fields": [{
        "label": "Electrical Specifications",
        "key": "Specs.Electrical",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Mechanical Specifications",
        "key": "Specs.Mechanical",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Installation Manual",
        "key": "Specs.InstallationManual",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "DDP Ref.",
        "key": "Specs.DDPRef",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Test Reports in Lieu of DDP",
        "key": "Specs.TestReportsLieuDDP",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Other documents in lieu of test reports",
        "key": "Specs.OtherDocsLieuTestReports",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Reference documents / drawings",
        "key": "Specs.ReferenceDocsDrawings",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Limitations from above",
        "key": "Specs.LimitationsFromAbove",
        "class": "md:col-span-3",
        "grow": true
    }],
    "actions": [{
        "label": "Edit Equipment Specifications",
        action(args) {
            editEquipmentSpecs({ ...args, item: args.pageItem });
        },
        show({ item }) {
            return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
        }
    }],
    show({ pageItem }) {
        return pageItem.EADTClassification === "Detailed";
    }
};

// --- Software Assessment ---

const softwareAssessment = {
    "label": "Software Assessment",
    "field": true,
    "fields": [{
        "label": "1. Contains Software?",
        "key": "Software.ContainsSoftware",
        "grow": true
    }, {
        "label": "2. Developed to Standards?",
        "key": "Software.DevelopedToStandards",
        "grow": true
    }, {
        "label": "3. Standard Used",
        "key": "Software.Standard",
        "grow": true
    }, {
        "label": "4. DAL",
        "key": "Software.DAL",
        "grow": true
    }, {
        "label": "5. DAL is A–D?",
        "key": "Software.DALIsAToD",
        "grow": true
    }, {
        "label": "6. Accomplishment Summary Ref.",
        "key": "Software.AccomplishmentSummaryRef",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "7. Open Problem Reports?",
        "key": "Software.OpenProblemReports",
        "grow": true
    }, {
        "label": "8. Avionic Equipment?",
        "key": "Software.IsAvionicEquipment",
        "grow": true
    }, {
        "label": "9. Has (E)TSO?",
        "key": "Software.HasETSO",
        "grow": true
    }],
    "actions": [{
        "label": "Edit Software Assessment",
        action(args) {
            editSoftwareAssessment({ ...args, item: args.pageItem });
        },
        show({ item }) {
            return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
        }
    }],
    show({ pageItem }) {
        return pageItem.EADTClassification === "Detailed";
    }
};

// --- AEH Assessment ---

const aehAssessment = {
    "label": "AEH Assessment",
    "field": true,
    "fields": [{
        "label": "1. Contains AEH?",
        "key": "AEH.ContainsAEH",
        "grow": true
    }, {
        "label": "2. Can be classified as simple?",
        "key": "AEH.IsSimple",
        "grow": true
    }, {
        "label": "3. Simple Details",
        "key": "AEH.SimpleDetails",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "4. Classified as CEH?",
        "key": "AEH.IsCEH",
        "grow": true
    }, {
        "label": "5. CEH Details",
        "key": "AEH.CEHDetails",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "6. CEH Developed to Standards?",
        "key": "AEH.CEHDevelopedToStandards",
        "grow": true
    }, {
        "label": "7. CEH Standard Used",
        "key": "AEH.CEHStandard",
        "grow": true
    }, {
        "label": "8. CEH DAL",
        "key": "AEH.CEHDAL",
        "grow": true
    }, {
        "label": "9. CEH DAL is A–D?",
        "key": "AEH.CEHDALIsAToD",
        "grow": true
    }, {
        "label": "10. CEH Accomplishment Summary Ref.",
        "key": "AEH.CEHAccomplishmentSummaryRef",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "11. CEH Open Problem Reports?",
        "key": "AEH.CEHOpenProblemReports",
        "grow": true
    }, {
        "label": "12. Avionic Equipment?",
        "key": "AEH.IsAvionicEquipment",
        "grow": true
    }, {
        "label": "13. Has (E)TSO?",
        "key": "AEH.HasETSO",
        "grow": true
    }],
    "actions": [{
        "label": "Edit AEH Assessment",
        action(args) {
            editAEHAssessment({ ...args, item: args.pageItem });
        },
        show({ item }) {
            return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
        }
    }],
    show({ pageItem }) {
        return pageItem.EADTClassification === "Detailed";
    }
};

// --- Additional Data & Limitations ---

const finalSection = {
    "label": "Additional Data",
    "field": true,
    "fields": [{
        "label": "Additional Data",
        "key": "AdditionalData",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Required Maintenance",
        "key": "RequiredMaintenance",
        "class": "md:col-span-3",
        "grow": true
    }, {
        "label": "Suitability for Proposed Application",
        "key": "Suitability",
        "class": "md:col-span-3",
        "grow": true
    }],
    "actions": [{
        "label": "Edit Additional Data",
        action(args) {
            editFinalSection({ ...args, item: args.pageItem });
        },
        show({ item }) {
            return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
        }
    }],
    show({ pageItem }) {
        return pageItem.EADTClassification === "Detailed";
    }
};

// --- Equipment Limitations (shared) ---

const limitations = {
    "label": "Equipment Limitations",
    "field": true,
    "fields": limitationFields.map(f => ({ ...f })),
    "actions": [{
        "label": "Edit Equipment Limitations",
        action(args) {
            editLimitations({ ...args, item: args.pageItem });
        },
        show({ item }) {
            return [item.Completed !== true, "You cannot edit a complete EADT, you must revise it before it is editing."]
        }
    }]
};

// --- Exported sections ---

const isDetailed = ({ pageItem }) => pageItem.EADTClassification === "Detailed";

export default {
    "Standard EADT Data": {
        "attachables": makeFieldSideTables(simplifiedFields),
        show({ pageItem }) {
            return pageItem.EADTClassification === "Simplified";
        }
    },
    "Detailed EADT Data": {
        "attachables": [designStandards, equipmentSpecs, softwareAssessment, aehAssessment, finalSection]
    },
    "Equipment Limitations": {
        "attachables": [limitations]
    }
}
